import { Fragment, type ReactNode, useContext } from 'react'
import * as Mtds from '@mattilsynet/design/react'
import { DetailsLayoutActionContext } from '../details-layout/DetailsLayout'

export type ScreenHeaderProperty = {
  label: string
  value: ReactNode
  icon?: ReactNode
  tooltip?: string
  href?: string
}

export type ScreenHeaderProps = {
  title: ReactNode
  leading?: ReactNode
  titleActions?: ReactNode
  actions?: ReactNode
  properties?: ScreenHeaderProperty[]
  description?: ReactNode
  hideDescriptionBehindHelpText?: boolean
  children?: ReactNode
}

type ScreenPropertiesProps = {
  properties: ScreenHeaderProperty[]
  children?: ReactNode
  hideTitle?: boolean
  unstyled?: boolean
}

const PropertyValue = ({ property }: { property: ScreenHeaderProperty }) => {
  const content = <span>{property.value ?? '-'}</span>

  if (property.href != null) return <a href={property.href}>{content}</a>

  return content
}

export const ScreenPropertiesList = ({ properties }: ScreenPropertiesProps) => {
  if (!properties.length) return null

  return (
    <Mtds.Grid
      data-gap={undefined}
      className='grid-cols-[max-content_minmax(0,1fr)] gap-x-14 gap-y-4'
    >
      {properties.map((property) => (
        <Fragment key={property.label}>
          <span
            data-tooltip={property.tooltip}
            className='inline-flex items-center self-start gap-3 whitespace-nowrap'
          >
            {property.icon}
            {property.label}
          </span>
          <span className='min-w-0'>
            <PropertyValue property={property} />
          </span>
        </Fragment>
      ))}
    </Mtds.Grid>
  )
}

export const ScreenPropertiesCard = ({
  properties,
  children,
  hideTitle,
  unstyled
}: ScreenPropertiesProps) => {
  if (!properties.length && !children) return null

  return (
    <Mtds.Grid
      data-gap='4'
      className={unstyled ? 'content-start' : 'h-full content-start'}
    >
      {!hideTitle && (
        <Mtds.Heading as='h2' data-size='sm'>
          Detaljer
        </Mtds.Heading>
      )}
      {!!properties.length && (
        <Mtds.Table data-fixed>
          <tbody>
            {properties.map((property) => (
              <tr key={property.label}>
                <th>{property.label}</th>
                <td>
                  <PropertyValue property={property} />
                </td>
              </tr>
            ))}
          </tbody>
        </Mtds.Table>
      )}
      {children && (
        <Mtds.Grid
          data-gap='8'
          className={properties.length ? 'pt-2' : undefined}
        >
          {children}
        </Mtds.Grid>
      )}
    </Mtds.Grid>
  )
}

export const ScreenHeader = ({
  title,
  leading,
  titleActions,
  actions,
  properties = [],
  description,
  hideDescriptionBehindHelpText = false,
  children
}: ScreenHeaderProps) => {
  const detailsAction = useContext(DetailsLayoutActionContext)

  return (
    <Mtds.Grid>
      <Mtds.Flex
        data-justify='space-between'
        data-align='start'
        className='flex-wrap-reverse'
      >
        <Mtds.Flex
          data-align='center'
          data-gap={4}
          className='min-w-0 flex-wrap-reverse'
        >
          {leading && <div className='shrink-0'>{leading}</div>}
          <Mtds.Heading data-size='md' as='h1' className='min-w-0'>
            {title}
          </Mtds.Heading>
          {description && hideDescriptionBehindHelpText && (
            <Mtds.HelpText>{description}</Mtds.HelpText>
          )}
          {titleActions && (
            <Mtds.Flex data-align='center' className='ml-auto shrink-0'>
              {titleActions}
            </Mtds.Flex>
          )}
        </Mtds.Flex>
        {(actions || detailsAction) && (
          <Mtds.Flex data-align='center' className='ml-auto shrink-0'>
            {actions}
            {detailsAction}
          </Mtds.Flex>
        )}
      </Mtds.Flex>
      <ScreenPropertiesList properties={properties} />
      {description && !hideDescriptionBehindHelpText && (
        <Mtds.Prose>{description}</Mtds.Prose>
      )}
      {children}
    </Mtds.Grid>
  )
}
